<template>
  <div class="create-test-page">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
      <div class="floating-shape shape-4"></div>
      <div class="floating-shape shape-5"></div>
    </div>

    <!-- Header -->
    <div class="page-header">
      <div class="header-glow"></div>
      <div class="container-fluid">
        <div class="d-flex align-items-center justify-content-between">
          <div class="header-content">
            <div class="header-icon-wrapper">
              <div class="header-icon">
                <i class="fas fa-pen-fancy"></i>
              </div>
            </div>
            <div>
              <h1 class="page-title">
                {{ $t("qbank.new_test.create_custom") }}
              </h1>
              <p class="page-subtitle">
                {{ $t("qbank.new_test.configure_test") }}
              </p>
            </div>
          </div>
          <button @click="$router.go(-1)" class="btn btn-back">
            <i class="fas fa-arrow-left"></i>
            <span>{{ $t("common.back") }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <div class="spinner-ring"></div>
        <i class="fas fa-brain spinner-icon"></i>
      </div>
      <p class="loading-text">{{ $t("common.loading") }}</p>
    </div>

    <!-- Main Content -->
    <div v-else class="content-container">
      <div class="container-fluid">
        <div class="row justify-content-center">
          <div class="col-lg-10 col-xl-8">
            <!-- Progress Indicator -->
            <div class="progress-indicator">
              <div class="progress-step" :class="{ completed: true }">
                <div class="step-icon"><i class="fas fa-cog"></i></div>
                <span>{{ $t("qbank.new_test.test_type") }}</span>
              </div>
              <div
                class="progress-line"
                :class="{ active: selectedLessons.length > 0 }"
              ></div>
              <div
                class="progress-step"
                :class="{ completed: selectedLessons.length > 0 }"
              >
                <div class="step-icon"><i class="fas fa-book"></i></div>
                <span>{{ $t("qbank.new_test.subject") }}</span>
              </div>
              <div
                class="progress-line"
                :class="{ active: canStartTest }"
              ></div>
              <div class="progress-step" :class="{ completed: canStartTest }">
                <div class="step-icon"><i class="fas fa-rocket"></i></div>
                <span>{{ $t("qbank.new_test.start_test") }}</span>
              </div>
            </div>

            <div class="test-form-card">
              <!-- Stats Summary -->
              <div class="stats-summary">
                <div class="stat-item">
                  <div class="stat-icon">
                    <i class="fas fa-question-circle"></i>
                  </div>
                  <div class="stat-info">
                    <span class="stat-value">{{ maxQuestions }}</span>
                    <span class="stat-label">{{ $t("qbank.questions") }}</span>
                  </div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-item">
                  <div class="stat-icon">
                    <i class="fas fa-layer-group"></i>
                  </div>
                  <div class="stat-info">
                    <span class="stat-value">{{ categories.length }}</span>
                    <span class="stat-label">{{
                      $t("qbank.plan.categories")
                    }}</span>
                  </div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-item">
                  <div class="stat-icon"><i class="fas fa-chart-bar"></i></div>
                  <div class="stat-info">
                    <span class="stat-value">{{
                      difficultyLevels.length
                    }}</span>
                    <span class="stat-label">{{
                      $t("qbank.new_test.difficulty_levels")
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Test Type Section -->
              <div class="form-section animate-in" style="--delay: 0.1s">
                <h3 class="section-title">
                  <div class="section-icon-wrapper">
                    <i class="fas fa-sliders-h"></i>
                  </div>
                  {{ $t("qbank.new_test.test_type") }}
                </h3>
                <div class="test-type-options">
                  <div
                    class="type-option"
                    :class="{ active: quizMode === 'timed' }"
                    @click="quizMode = 'timed'"
                  >
                    <div class="type-icon-wrapper">
                      <i class="fas fa-clock"></i>
                    </div>
                    <div class="option-content">
                      <span class="option-label">{{
                        $t("qbank.mode_timed")
                      }}</span>
                      <p class="option-desc">
                        {{ $t("qbank.new_test.timed_desc") }}
                      </p>
                    </div>
                    <div class="option-check">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                  <div
                    class="type-option"
                    :class="{ active: quizMode === 'tutor' }"
                    @click="quizMode = 'tutor'"
                  >
                    <div class="type-icon-wrapper tutor">
                      <i class="fas fa-graduation-cap"></i>
                    </div>
                    <div class="option-content">
                      <span class="option-label">{{
                        $t("qbank.mode_tutor")
                      }}</span>
                      <p class="option-desc">
                        {{ $t("qbank.new_test.tutor_desc") }}
                      </p>
                    </div>
                    <div class="option-check">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Question Type Section -->
              <div class="form-section animate-in" style="--delay: 0.2s">
                <h3 class="section-title">
                  <div class="section-icon-wrapper purple">
                    <i class="fas fa-filter"></i>
                  </div>
                  {{ $t("qbank.new_test.question_type") }}
                </h3>

                <!-- Unused (New) Questions -->
                <div
                  class="question-mode-option"
                  :class="{ selected: unusedSelected }"
                  @click="unusedSelected = !unusedSelected"
                >
                  <div class="mode-icon new">
                    <i class="fas fa-star"></i>
                  </div>
                  <div class="mode-content">
                    <span class="mode-label">{{
                      $t("qbank.new_test.new_questions")
                    }}</span>
                    <span class="mode-desc">{{ $t("qbank.type.unused") }}</span>
                  </div>
                  <div class="mode-right">
                    <span class="mode-count">{{ unusedMode.count }}</span>
                    <div
                      class="mode-checkbox"
                      :class="{ checked: unusedSelected }"
                    >
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                </div>

                <!-- Used (Previously Answered) Questions -->
                <div
                  class="question-mode-option"
                  :class="{ selected: usedSelected }"
                  @click="openUsedModesModal"
                >
                  <div class="mode-icon used">
                    <i class="fas fa-redo"></i>
                  </div>
                  <div class="mode-content">
                    <span class="mode-label">{{
                      $t("qbank.new_test.previously_answered")
                    }}</span>
                    <span class="mode-desc" v-if="selectedUsedModes.length > 0">
                      {{ selectedUsedModes.length }}
                      {{ $t("qbank.new_test.selected") }}
                    </span>
                    <span class="mode-desc" v-else>{{
                      $t("qbank.type.used")
                    }}</span>
                  </div>
                  <div class="mode-right">
                    <span class="mode-count">{{ totalUsedCount }}</span>
                    <div
                      class="mode-checkbox"
                      :class="{
                        checked: usedSelected,
                        partial:
                          usedSelected &&
                          selectedUsedModes.length < usedModes.length,
                      }"
                    >
                      <i
                        :class="
                          usedSelected &&
                          selectedUsedModes.length < usedModes.length
                            ? 'fas fa-minus'
                            : 'fas fa-check'
                        "
                      ></i>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Subject/Lessons Section -->
              <div class="form-section animate-in" style="--delay: 0.3s">
                <h3 class="section-title">
                  <div class="section-icon-wrapper blue">
                    <i class="fas fa-book-open"></i>
                  </div>
                  {{ $t("qbank.new_test.subject") }}
                </h3>
                <div
                  class="subject-selector"
                  :class="{ selected: selectedLessons.length > 0 }"
                  @click="openLessonModal"
                >
                  <div class="selector-icon">
                    <i class="fas fa-book"></i>
                  </div>
                  <div class="selector-content">
                    <span
                      class="selector-text"
                      :class="{ placeholder: selectedLessons.length === 0 }"
                    >
                      {{
                        selectedLessons.length === 0
                          ? $t("qbank.new_test.select_subject")
                          : `${selectedLessons.length} ${$t(
                              "qbank.new_test.lessons_selected"
                            )}`
                      }}
                    </span>
                    <span
                      class="selector-hint"
                      v-if="selectedLessons.length === 0"
                    >
                      {{ $t("qbank.choose_lessons") }}
                    </span>
                  </div>
                  <div class="selector-arrow">
                    <i class="fas fa-chevron-left"></i>
                  </div>
                </div>
              </div>

              <!-- Question Count Section -->
              <div class="form-section animate-in" style="--delay: 0.4s">
                <h3 class="section-title">
                  <div class="section-icon-wrapper orange">
                    <i class="fas fa-hashtag"></i>
                  </div>
                  {{ $t("qbank.new_test.number_of_questions") }}
                </h3>
                <div class="question-count-wrapper">
                  <div class="question-count-control">
                    <button
                      class="count-btn minus"
                      @click="decrementCount"
                      :disabled="totalQuestions <= 1"
                    >
                      <i class="fas fa-minus"></i>
                    </button>
                    <div class="count-display">
                      <input
                        type="number"
                        v-model.number="totalQuestions"
                        class="count-input"
                        min="1"
                        :max="maxQuestions"
                        @change="validateInputCount"
                      />
                      <div class="count-label">{{ $t("qbank.questions") }}</div>
                    </div>
                    <button
                      class="count-btn plus"
                      @click="incrementCount"
                      :disabled="totalQuestions >= maxQuestions"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                  </div>
                  <div class="count-slider">
                    <input
                      type="range"
                      v-model.number="totalQuestions"
                      min="1"
                      :max="maxQuestions"
                      class="slider-input"
                    />
                    <div class="slider-labels">
                      <span>1</span>
                      <span
                        >{{ $t("qbank.new_test.max_available") }}:
                        {{ maxQuestions }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Advanced Options Button -->
              <div class="form-section animate-in" style="--delay: 0.5s">
                <button class="btn btn-advanced" @click="openAdvancedOptions">
                  <div class="advanced-icon">
                    <i class="fas fa-sliders-h"></i>
                  </div>
                  <div class="advanced-content">
                    <span class="advanced-title">{{
                      $t("qbank.new_test.advanced_options")
                    }}</span>
                    <span class="advanced-desc"
                      >{{ $t("qbank.new_test.difficulty_levels") }} &
                      {{ $t("qbank.new_test.sources") }}</span
                    >
                  </div>
                  <div class="advanced-badge-wrapper" v-if="hasAdvancedFilters">
                    <span class="advanced-badge">{{
                      advancedFiltersCount
                    }}</span>
                  </div>
                  <i class="fas fa-chevron-left advanced-arrow"></i>
                </button>
              </div>

              <!-- Start Test Button -->
              <div class="form-section animate-in" style="--delay: 0.6s">
                <button
                  class="btn btn-start-test"
                  :class="{ ready: canStartTest }"
                  :disabled="!canStartTest || submitting"
                  @click="startTest"
                >
                  <div class="btn-bg"></div>
                  <div class="btn-content">
                    <span
                      v-if="submitting"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <i v-else class="fas fa-rocket"></i>
                    <span>{{ $t("qbank.new_test.start_test") }}</span>
                  </div>
                  <div class="btn-shine"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lesson Selection Modal -->
    <LessonSelectionModal
      ref="lessonModal"
      :categories="categories"
      :selected-lessons="selectedLessons"
      @update:selected-lessons="updateSelectedLessons"
    />

    <!-- Used Modes Selection Modal -->
    <UsedModesModal
      ref="usedModesModal"
      :used-modes="usedModes"
      :unused-mode="unusedMode"
      :selected-modes="selectedUsedModes"
      :unused-selected="unusedSelected"
      @update:selected-modes="updateSelectedUsedModes"
      @update:unused-selected="updateUnusedSelected"
    />

    <!-- Advanced Options Modal -->
    <AdvancedOptionsModal
      ref="advancedModal"
      :difficulty-levels="difficultyLevels"
      :sources="sources"
      :question-types="questionTypes"
      :selected-difficulty-levels="selectedDifficultyLevels"
      :selected-sources="selectedSources"
      :selected-question-types="selectedQuestionTypes"
      @save="saveAdvancedOptions"
    />
  </div>
</template>

<script>
import { useQBankStore } from "@/stores/qbank";
import LessonSelectionModal from "./LessonSelectionModal.vue";
import UsedModesModal from "./UsedModesModal.vue";
import AdvancedOptionsModal from "./AdvancedOptionsModal.vue";
import Swal from "sweetalert2";

export default {
  name: "CreateCustomTest",
  components: {
    LessonSelectionModal,
    UsedModesModal,
    AdvancedOptionsModal,
  },
  setup() {
    const qbankStore = useQBankStore();
    return { qbankStore };
  },
  data() {
    return {
      loading: true,
      submitting: false,
      quizMode: "timed",
      unusedSelected: true,
      usedSelected: false,
      selectedUsedModes: [],
      selectedLessons: [],
      totalQuestions: 10,
      selectedDifficultyLevels: [],
      selectedSources: [],
      selectedQuestionTypes: [],
      availableQuestions: null,
      validatingCount: false,
    };
  },
  computed: {
    subscriptionPlanId() {
      return this.$route.query.subscriptionId;
    },
    store() {
      return this.qbankStore;
    },
    statistics() {
      return this.store.statistics;
    },
    categories() {
      return this.store.categories;
    },
    unusedMode() {
      return this.store.unusedMode;
    },
    usedModes() {
      return this.store.usedModes;
    },
    totalUsedCount() {
      return this.store.totalUsedCount;
    },
    difficultyLevels() {
      return this.store.difficultyLevels;
    },
    sources() {
      return this.store.statisticsSources;
    },
    questionTypes() {
      return this.store.questionTypes;
    },
    maxQuestions() {
      // Use validated count if available (even if 0), otherwise fall back to store total
      if (this.availableQuestions !== null) {
        return this.availableQuestions;
      }
      return this.store.totalQuestions || 100;
    },
    canStartTest() {
      const hasQuestionModes =
        this.unusedSelected || this.selectedUsedModes.length > 0;
      const hasLessons = this.selectedLessons.length > 0;
      const hasValidCount =
        this.totalQuestions > 0 && this.totalQuestions <= this.maxQuestions;
      return hasQuestionModes && hasLessons && hasValidCount;
    },
    hasAdvancedFilters() {
      return (
        this.selectedDifficultyLevels.length > 0 ||
        this.selectedSources.length > 0 ||
        this.selectedQuestionTypes.length > 0
      );
    },
    advancedFiltersCount() {
      return (
        this.selectedDifficultyLevels.length +
        this.selectedSources.length +
        this.selectedQuestionTypes.length
      );
    },
  },
  watch: {
    selectedLessons: {
      handler() {
        this.fetchAvailableQuestions();
      },
      deep: true,
    },
    unusedSelected() {
      this.fetchAvailableQuestions();
    },
    usedSelected() {
      this.fetchAvailableQuestions();
    },
    selectedUsedModes: {
      handler() {
        this.fetchAvailableQuestions();
      },
      deep: true,
    },
    selectedDifficultyLevels: {
      handler() {
        this.fetchAvailableQuestions();
      },
      deep: true,
    },
    selectedSources: {
      handler() {
        this.fetchAvailableQuestions();
      },
      deep: true,
    },
    selectedQuestionTypes: {
      handler() {
        this.fetchAvailableQuestions();
      },
      deep: true,
    },
  },
  async mounted() {
    if (!this.subscriptionPlanId) {
      this.$router.push("/qbank");
      return;
    }

    this.store.setSubscriptionPlanId(this.subscriptionPlanId);

    try {
      await Promise.all([
        this.store.loadStatistics(),
        this.store.loadCategories(),
      ]);
    } catch (error) {
      console.error("Failed to load data:", error);
      Swal.fire({
        icon: "error",
        title: this.$t("common.error"),
        text: this.$t("qbank.new_test.load_error"),
      });
    } finally {
      this.loading = false;
      // Call validate-count immediately after loading to get initial count
      this.$nextTick(() => {
        this.fetchAvailableQuestions();
      });
    }
  },
  methods: {
    openLessonModal() {
      this.$refs.lessonModal.show();
    },
    openUsedModesModal() {
      this.$refs.usedModesModal.show();
    },
    openAdvancedOptions() {
      this.$refs.advancedModal.show();
    },
    updateSelectedLessons(lessons) {
      this.selectedLessons = lessons;
    },
    updateSelectedUsedModes(modes) {
      this.selectedUsedModes = modes;
      this.usedSelected = modes.length > 0;
    },
    updateUnusedSelected(selected) {
      this.unusedSelected = selected;
    },
    saveAdvancedOptions({ difficultyLevels, sources, questionTypes }) {
      this.selectedDifficultyLevels = difficultyLevels;
      this.selectedSources = sources;
      this.selectedQuestionTypes = questionTypes;
    },
    incrementCount() {
      if (this.totalQuestions < this.maxQuestions) {
        this.totalQuestions++;
      }
    },
    decrementCount() {
      if (this.totalQuestions > 1) {
        this.totalQuestions--;
      }
    },
    validateInputCount() {
      if (this.totalQuestions < 1) {
        this.totalQuestions = 1;
      } else if (this.totalQuestions > this.maxQuestions) {
        this.totalQuestions = this.maxQuestions;
      }
    },
    async fetchAvailableQuestions() {
      // Skip only if still loading initial data or no subscription plan
      if (this.loading || !this.subscriptionPlanId) {
        return;
      }

      // Build question modes array
      const questionModes = [];
      if (this.unusedSelected) {
        questionModes.push("unused");
      }
      if (this.selectedUsedModes.length > 0) {
        questionModes.push(...this.selectedUsedModes);
      }

      // Build filters - ALWAYS call API even if empty
      const filters = {
        lessons: this.selectedLessons.map((l) => l.id),
        question_modes: questionModes,
      };

      if (this.selectedDifficultyLevels.length > 0) {
        filters.difficulty_levels = this.selectedDifficultyLevels;
      }

      if (this.selectedSources.length > 0) {
        filters.sources = this.selectedSources;
      }

      if (this.selectedQuestionTypes.length > 0) {
        filters.question_types = this.selectedQuestionTypes;
      }

      this.validatingCount = true;
      try {
        const count = await this.store.validateQuestionCount(
          filters,
          this.subscriptionPlanId
        );
        // Set availableQuestions regardless of value (can be 0)
        if (typeof count === "number" && count >= 0) {
          this.availableQuestions = count;
          // Adjust totalQuestions if it exceeds the new max
          if (
            this.availableQuestions > 0 &&
            this.totalQuestions > this.availableQuestions
          ) {
            this.totalQuestions = Math.max(1, this.availableQuestions);
          }
        } else {
          // If API returns null/error, reset to null
          this.availableQuestions = null;
        }
      } catch (error) {
        console.error("Failed to validate question count:", error);
        this.availableQuestions = null;
      } finally {
        this.validatingCount = false;
      }
    },
    async startTest() {
      if (!this.canStartTest) return;

      // Build question modes array
      const questionModes = [];
      if (this.unusedSelected) {
        questionModes.push("unused");
      }
      if (this.selectedUsedModes.length > 0) {
        questionModes.push(...this.selectedUsedModes);
      }

      if (questionModes.length === 0) {
        Swal.fire({
          icon: "warning",
          title: this.$t("qbank.new_test.validation_error"),
          text: this.$t("qbank.new_test.select_question_modes"),
        });
        return;
      }

      if (this.selectedLessons.length === 0) {
        Swal.fire({
          icon: "warning",
          title: this.$t("qbank.new_test.validation_error"),
          text: this.$t("qbank.please_select_lessons"),
        });
        return;
      }

      const testData = {
        lessons: this.selectedLessons.map((l) => l.id),
        quiz_mode: this.quizMode,
        total_questions: this.totalQuestions,
        question_modes: questionModes,
      };

      if (this.selectedDifficultyLevels.length > 0) {
        testData.difficulty_levels = this.selectedDifficultyLevels;
      }

      if (this.selectedSources.length > 0) {
        testData.sources = this.selectedSources;
      }

      if (this.selectedQuestionTypes.length > 0) {
        testData.question_types = this.selectedQuestionTypes;
      }

      this.submitting = true;

      try {
        const result = await this.store.createCustomTest(testData);

        Swal.fire({
          icon: "success",
          title: this.$t("qbank.new_test.test_created"),
          text: this.$t("qbank.new_test.redirecting"),
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          // Redirect to quiz based on mode
          const test = result.question_bank || result;
          const quizModeNum = this.quizMode === "tutor" ? 1 : 2;
          const route =
            quizModeNum === 1 ? "qbank-quiz-tutor" : "qbank-quiz-timed";

          this.$router.push({
            path: `/${route}`,
            query: {
              qref: test.id,
              num: test.answered_questions_count || 0,
              mode: quizModeNum,
              total: test.total_questions_count || this.totalQuestions,
              subscriptionId: this.subscriptionPlanId,
            },
          });
        });
      } catch (error) {
        console.error("Failed to create test:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: error.message || this.$t("qbank.new_test.create_error"),
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   CREATE TEST PAGE - CLEAN GREEN THEME
   ============================================ */

.create-test-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 50%, #f8fafc 100%);
  position: relative;
}

/* Animated Background - Subtle */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.floating-shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c744, #00a83b);
  opacity: 0.03;
}

.shape-1 {
  width: 500px;
  height: 500px;
  top: -200px;
  right: -150px;
}

.shape-2 {
  width: 400px;
  height: 400px;
  bottom: -100px;
  left: -100px;
}

.shape-3,
.shape-4,
.shape-5 {
  display: none;
}

/* Header */
.page-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 24px 20px;
  color: white;
  position: relative;
  z-index: 10;
}

.header-glow {
  display: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-wrapper {
  position: relative;
}

.header-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.page-subtitle {
  font-size: 0.85rem;
  opacity: 0.9;
  margin: 3px 0 0;
}

.btn-back {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  position: relative;
  z-index: 5;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #00c744;
  animation: spin 1s linear infinite;
}

.spinner-ring:nth-child(2),
.spinner-ring:nth-child(3) {
  display: none;
}

.spinner-icon {
  display: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 16px;
  font-size: 1rem;
  font-weight: 600;
  color: #64748b;
}

/* Content Container */
.content-container {
  position: relative;
  z-index: 5;
  padding: 24px 0 40px;
}

/* Progress Indicator */
.progress-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0.4;
  transition: all 0.3s ease;
}

.progress-step.completed {
  opacity: 1;
}

.progress-step .step-icon {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #64748b;
  transition: all 0.3s ease;
}

.progress-step.completed .step-icon {
  background: #00c744;
  color: white;
}

.progress-step span {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 600;
}

.progress-line {
  width: 50px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 12px;
  border-radius: 1px;
  transition: all 0.3s ease;
}

.progress-line.active {
  background: #00c744;
}

/* Form Card */
.test-form-card {
  background: white;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* Stats Summary */
.stats-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 14px;
  margin-bottom: 24px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stat-icon {
  width: 40px;
  height: 40px;
  background: #00c744;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 35px;
  background: #e2e8f0;
}

/* Form Section */
.form-section {
  margin-bottom: 24px;
}

.animate-in {
  /* Remove animation for simplicity */
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-icon-wrapper {
  width: 34px;
  height: 34px;
  background: #00c744;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
}

.section-icon-wrapper.purple {
  background: #6366f1;
}

.section-icon-wrapper.blue {
  background: #0ea5e9;
}

.section-icon-wrapper.orange {
  background: #f59e0b;
}

/* Test Type Options */
.test-type-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.type-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.type-option:hover {
  border-color: #00c744;
}

.type-option.active {
  border-color: #00c744;
  background: #f0fdf4;
}

.type-icon-wrapper {
  width: 50px;
  height: 50px;
  background: #00c744;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  margin-bottom: 12px;
}

.type-icon-wrapper.tutor {
  background: #6366f1;
}

.option-content {
  text-align: center;
}

.option-label {
  font-weight: 600;
  color: #1e293b;
  display: block;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.option-desc {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.option-check {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 18px;
  color: #00c744;
  opacity: 0;
  transition: all 0.2s ease;
}

.type-option.active .option-check {
  opacity: 1;
}

/* Question Mode Options */
.question-mode-option {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 10px;
  gap: 14px;
}

.question-mode-option:hover {
  border-color: #00c744;
}

.question-mode-option.selected {
  border-color: #00c744;
  background: #f0fdf4;
}

.mode-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  flex-shrink: 0;
  background: #00c744;
}

.mode-icon.new {
  background: #10b981;
}

.mode-icon.used {
  background: #f59e0b;
}

.mode-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mode-label {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.mode-desc {
  font-size: 0.75rem;
  color: #64748b;
}

.mode-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mode-count {
  background: #e2e8f0;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
}

.mode-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mode-checkbox.checked {
  background: #00c744;
  border-color: #00c744;
}

.mode-checkbox.partial {
  background: #00c744;
  border-color: #00c744;
}

.mode-checkbox i {
  color: white;
  font-size: 11px;
  opacity: 0;
  transition: all 0.2s ease;
}

.mode-checkbox.checked i,
.mode-checkbox.partial i {
  opacity: 1;
}

/* Subject Selector */
.subject-selector {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 14px;
}

.subject-selector:hover {
  border-color: #00c744;
}

.subject-selector.selected {
  border-color: #00c744;
  background: #f0fdf4;
}

.selector-icon {
  width: 42px;
  height: 42px;
  background: #00c744;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  flex-shrink: 0;
}

.selector-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.selector-text {
  font-weight: 600;
  color: #1e293b !important;
  font-size: 0.9rem;
  background: transparent !important;
  -webkit-text-fill-color: #1e293b !important;
}

.selector-text.placeholder {
  color: #475569 !important;
  -webkit-text-fill-color: #475569 !important;
  background: transparent !important;
  opacity: 1 !important;
}

.selector-hint {
  font-size: 0.75rem;
  color: #64748b !important;
  -webkit-text-fill-color: #64748b !important;
  background: transparent !important;
}

.selector-content {
  background: transparent !important;
}

.selector-content * {
  background: transparent !important;
}

.selector-arrow {
  width: 32px;
  height: 32px;
  background: #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
}

.subject-selector:hover .selector-arrow {
  background: #00c744;
  color: white;
}

/* Question Count */
.question-count-wrapper {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
}

.question-count-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
}

.count-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.count-btn.minus {
  background: #e2e8f0;
  color: #64748b;
}

.count-btn.minus:hover:not(:disabled) {
  background: #cbd5e1;
}

.count-btn.plus {
  background: #00c744;
  color: white;
}

.count-btn.plus:hover:not(:disabled) {
  background: #00a83b;
}

.count-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.count-display {
  text-align: center;
}

.count-input {
  width: 100px;
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  border: none;
  background: transparent;
  outline: none;
  -moz-appearance: textfield;
}

.count-input::-webkit-outer-spin-button,
.count-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
  display: none;
}

.count-input::-moz-outer-spin-button,
.count-input::-moz-inner-spin-button {
  -moz-appearance: none;
  margin: 0;
  display: none;
}

.count-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.count-slider {
  padding: 0 5px;
}

.slider-input {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e2e8f0;
  outline: none;
  -webkit-appearance: none;
}

.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #00c744;
  cursor: pointer;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
}

/* Advanced Options Button */
.btn-advanced {
  width: 100%;
  padding: 14px 16px;
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
}

.btn-advanced:hover {
  border-color: #00c744;
  border-style: solid;
}

.advanced-icon {
  width: 42px;
  height: 42px;
  background: #00c744;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.advanced-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.advanced-title {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.advanced-desc {
  font-size: 0.75rem;
  color: #64748b;
}

.advanced-badge-wrapper {
  margin-right: 8px;
}

.advanced-badge {
  background: #00c744;
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.advanced-arrow {
  color: #94a3b8;
  font-size: 12px;
}

/* Start Test Button */
.btn-start-test {
  width: 100%;
  padding: 16px 24px;
  background: #cbd5e1;
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.btn-start-test.ready {
  background: #00c744;
}

.btn-start-test.ready:hover {
  background: #00a83b;
}

.btn-bg,
.btn-shine {
  display: none;
}

.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-start-test .btn-content i {
  color: #94a3b8;
}

.btn-start-test.ready .btn-content i {
  color: white;
}

.btn-start-test:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Force white icons inside colored backgrounds */
.header-icon i,
.stat-icon i,
.section-icon-wrapper i,
.type-icon-wrapper i,
.mode-icon i,
.selector-icon i,
.advanced-icon i,
.count-btn.plus i {
  color: white !important;
}

.btn-back i {
  color: white !important;
}

/* Hide external progress bar */
.subject-selector .progress,
.subject-selector .progress-bar,
.selector-content .progress,
.selector-content .progress-bar {
  display: none !important;
}

.progress-step .step-icon i {
  color: white !important;
}

/* RTL Support */
[dir="rtl"] .selector-arrow i,
[dir="rtl"] .advanced-arrow {
  transform: rotate(180deg);
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 18px 15px;
  }

  .header-icon {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .page-title {
    font-size: 1.2rem;
  }

  .test-form-card {
    padding: 20px 16px;
    border-radius: 16px;
  }

  .test-type-options {
    grid-template-columns: 1fr;
  }

  .stats-summary {
    flex-direction: column;
    gap: 12px;
  }

  .stat-divider {
    width: 80%;
    height: 1px;
  }

  .progress-indicator {
    flex-wrap: wrap;
    gap: 8px;
  }

  .progress-line {
    display: none;
  }

  .btn-back span {
    display: none;
  }
}

/* Dark Mode Support */
[data-bs-theme="dark"] .create-test-page,
.dark .create-test-page {
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .progress-indicator,
.dark .progress-indicator {
  background: #1e293b;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .progress-step span,
.dark .progress-step span {
  color: #94a3b8;
}

[data-bs-theme="dark"] .progress-step .step-icon,
.dark .progress-step .step-icon {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .progress-step.completed .step-icon,
.dark .progress-step.completed .step-icon {
  background: #00c744;
  color: white;
}

[data-bs-theme="dark"] .test-form-card,
.dark .test-form-card {
  background: #1e293b;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .stats-summary,
.dark .stats-summary {
  background: #334155;
}

[data-bs-theme="dark"] .stat-value,
.dark .stat-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .stat-label,
.dark .stat-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .stat-divider,
.dark .stat-divider {
  background: #475569;
}

[data-bs-theme="dark"] .section-title,
.dark .section-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .type-option,
.dark .type-option {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .type-option:hover,
.dark .type-option:hover {
  border-color: #00c744;
}

[data-bs-theme="dark"] .type-option.active,
.dark .type-option.active {
  background: rgba(0, 199, 68, 0.15);
  border-color: #00c744;
}

[data-bs-theme="dark"] .option-label,
.dark .option-label {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .option-desc,
.dark .option-desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .question-mode-option,
.dark .question-mode-option {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .question-mode-option:hover,
.dark .question-mode-option:hover {
  border-color: #00c744;
}

[data-bs-theme="dark"] .question-mode-option.selected,
.dark .question-mode-option.selected {
  background: rgba(0, 199, 68, 0.15);
  border-color: #00c744;
}

[data-bs-theme="dark"] .mode-label,
.dark .mode-label {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .mode-desc,
.dark .mode-desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .mode-count,
.dark .mode-count {
  background: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .mode-checkbox,
.dark .mode-checkbox {
  border-color: #64748b;
}

[data-bs-theme="dark"] .subject-selector,
.dark .subject-selector {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .subject-selector:hover,
.dark .subject-selector:hover {
  border-color: #00c744;
}

[data-bs-theme="dark"] .subject-selector.selected,
.dark .subject-selector.selected {
  background: rgba(0, 199, 68, 0.15);
  border-color: #00c744;
}

[data-bs-theme="dark"] .selector-text,
.dark .selector-text {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

[data-bs-theme="dark"] .selector-text.placeholder,
.dark .selector-text.placeholder {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

[data-bs-theme="dark"] .selector-hint,
.dark .selector-hint {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

[data-bs-theme="dark"] .selector-arrow,
.dark .selector-arrow {
  background: #475569;
  color: #94a3b8;
}

[data-bs-theme="dark"] .question-count-wrapper,
.dark .question-count-wrapper {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .count-input,
.dark .count-input {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .count-label,
.dark .count-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .count-btn.minus,
.dark .count-btn.minus {
  background: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .slider-input,
.dark .slider-input {
  background: #475569;
}

[data-bs-theme="dark"] .slider-labels,
.dark .slider-labels {
  color: #64748b;
}

[data-bs-theme="dark"] .btn-advanced,
.dark .btn-advanced {
  background: #334155;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn-advanced:hover,
.dark .btn-advanced:hover {
  border-color: #00c744;
  border-style: solid;
}

[data-bs-theme="dark"] .advanced-title,
.dark .advanced-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .advanced-desc,
.dark .advanced-desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-start-test,
.dark .btn-start-test {
  background: #475569;
}

[data-bs-theme="dark"] .btn-start-test.ready,
.dark .btn-start-test.ready {
  background: #00c744;
}

[data-bs-theme="dark"] .floating-shape,
.dark .floating-shape {
  opacity: 0.02;
}
</style>
